/**
 * Google AdSense Auto Ads Utility
 * Handles auto ads initialization and management across the application
 */

class AdSenseAutoAds {
  constructor() {
    this.isAutoAdsEnabled = false;
    this.isAdSenseLoaded = false;
    this.initPromise = null;
    this.debugMode = true; // Enable debugging by default
    this.initAttempts = 0;
    this.maxInitAttempts = 50; // 5 seconds max wait time
    this.autoAdsStatus = 'not_initialized';
    this.lastError = null;
    this.lastRefreshTime = 0;
    this.refreshCooldown = 2000; // 2 seconds between refreshes

    // Check if auto ads are already enabled globally
    if (typeof window !== 'undefined' && window._adsenseAutoAdsEnabled) {
      this.isAutoAdsEnabled = true;
      this.autoAdsStatus = 'enabled';
      this.log('ℹ️ Auto ads already enabled globally');
    }
  }

  /**
   * Initialize auto ads when AdSense is ready
   * @returns {Promise} Promise that resolves when auto ads are initialized
   */
  init() {
    if (this.initPromise) {
      this.log('Auto ads already initializing...');
      return this.initPromise;
    }

    this.log('Starting auto ads initialization...');
    this.autoAdsStatus = 'initializing';

    this.initPromise = new Promise((resolve, reject) => {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        const error = new Error('AdSense auto ads can only be initialized in browser environment');
        this.lastError = error;
        this.autoAdsStatus = 'error';
        this.log('Error: Not in browser environment');
        reject(error);
        return;
      }

      // Check if AdSense script is loaded
      const checkAdSenseLoaded = () => {
        this.initAttempts++;
        this.log(`Checking AdSense availability (attempt ${this.initAttempts}/${this.maxInitAttempts})`);

        if (typeof window.adsbygoogle !== 'undefined') {
          this.isAdSenseLoaded = true;
          this.log('AdSense script loaded successfully!');
          this.enableAutoAds()
            .then(() => {
              this.log('Auto ads enabled successfully!');
              resolve();
            })
            .catch((error) => {
              this.lastError = error;
              this.autoAdsStatus = 'error';
              this.log('Error enabling auto ads:', error);
              reject(error);
            });
        } else if (this.initAttempts >= this.maxInitAttempts) {
          const error = new Error('AdSense script failed to load within timeout period');
          this.lastError = error;
          this.autoAdsStatus = 'timeout';
          this.log('Error: AdSense script load timeout');
          reject(error);
        } else {
          // Wait a bit and check again
          setTimeout(checkAdSenseLoaded, 100);
        }
      };

      checkAdSenseLoaded();
    });

    return this.initPromise;
  }

  /**
   * Enable auto ads
   * @returns {Promise} Promise that resolves when auto ads are enabled
   */
  async enableAutoAds() {
    if (this.isAutoAdsEnabled) {
      this.log('Auto ads already enabled');
      return Promise.resolve();
    }

    this.log('Attempting to enable auto ads...');

    return new Promise((resolve, reject) => {
      try {
        if (typeof window.adsbygoogle !== 'undefined') {
          // Check if auto ads are already enabled by checking for the error message
          try {
            this.log('Pushing auto ads configuration to AdSense...');
            this.log('ℹ️ Note: Auto ads disabled by default in account - using override method...');

            // Clean auto ads override - enables basic auto ads without forced ad types
            (window.adsbygoogle = window.adsbygoogle || []).push({
              google_ad_client: "ca-pub-8326584585992829",
              enable_page_level_ads: true
              // No page_level_ad_types - let Google decide placement naturally
            });
            this.log('✅ Clean auto ads override enabled (no forced ad types)');

            // Create one strategic auto ad to ensure visibility
            setTimeout(() => {
              this.createSingleStrategicAd();
            }, 1000);

            this.isAutoAdsEnabled = true;
            this.autoAdsStatus = 'enabled';

            // Set global flag to prevent duplicate initializations
            if (typeof window !== 'undefined') {
              window._adsenseAutoAdsEnabled = true;
            }

            this.log('✅ AdSense auto ads enabled successfully');

            // Check if auto ads are actually working after a delay
            setTimeout(() => {
              this.checkAutoAdsStatus();
            }, 2000);

            resolve();
          } catch (innerError) {
            // Check if the error is about auto ads already being enabled
            if (innerError.message && innerError.message.includes('Only one \'enable_page_level_ads\' allowed per page')) {
              this.log('ℹ️ Auto ads already enabled on this page');
              this.isAutoAdsEnabled = true;
              this.autoAdsStatus = 'enabled';

              // Set global flag to prevent duplicate initializations
              if (typeof window !== 'undefined') {
                window._adsenseAutoAdsEnabled = true;
              }

              // Still check status after a delay
              setTimeout(() => {
                this.checkAutoAdsStatus();
              }, 2000);

              resolve();
            } else {
              // Different error, handle it normally
              throw innerError;
            }
          }
        } else {
          const error = new Error('AdSense not loaded');
          this.lastError = error;
          this.autoAdsStatus = 'error';
          reject(error);
        }
      } catch (error) {
        this.lastError = error;
        this.autoAdsStatus = 'error';
        this.log('❌ Failed to enable auto ads:', error);
        reject(error);
      }
    });
  }

  /**
   * Push a regular ad unit to the queue
   * @param {Object} adConfig - Ad configuration object
   */
  pushAd(adConfig = {}) {
    try {
      if (typeof window.adsbygoogle !== 'undefined') {
        (window.adsbygoogle = window.adsbygoogle || []).push(adConfig);
      }
    } catch (error) {
      console.warn('Failed to push ad:', error);
    }
  }

  /**
   * Refresh auto ads for SPA route changes
   * This tells Google to re-analyze the page content for auto ads placement
   */
  refreshAutoAds() {
    if (!this.isAutoAdsEnabled || typeof window === 'undefined') {
      this.log('⚠️ Cannot refresh auto ads - not enabled or not in browser');
      return;
    }

    // Debouncing: prevent too frequent refreshes
    const now = Date.now();
    if (now - this.lastRefreshTime < this.refreshCooldown) {
      this.log(`⏱️ Auto ads refresh skipped (cooldown: ${this.refreshCooldown - (now - this.lastRefreshTime)}ms remaining)`);
      return;
    }
    this.lastRefreshTime = now;

    try {
      this.log('🔄 Gentle auto ads refresh for SPA route change...');

      // Only notify Google of page change - no forced ad creation
      this.notifyPageChange();

    } catch (error) {
      this.log('❌ Failed to refresh auto ads:', error);
    }
  }

  /**
   * Clear stale ads that might be preventing new auto ads
   */
  clearStaleAds() {
    if (typeof document === 'undefined') return;

    try {
      // Remove filled auto ads that are no longer visible or relevant
      const filledAds = document.querySelectorAll('ins.adsbygoogle[data-adsbygoogle-status="done"]');
      const autoAds = Array.from(filledAds).filter(ad =>
        ad.getAttribute('data-ad-format') === 'auto' ||
        ad.getAttribute('data-ad-format') === 'autorelaxed'
      );

      if (autoAds.length > 0) {
        this.log(`🧹 Found ${autoAds.length} existing auto ads to potentially refresh`);

        // For SPAs, we don't actually remove them, but reset their status
        // This allows Google to re-evaluate them
        autoAds.forEach(ad => {
          if (ad.style.display === 'none' || ad.offsetParent === null) {
            // Hidden ads can be reset
            ad.removeAttribute('data-adsbygoogle-status');
            this.log('🔄 Reset hidden auto ad for re-evaluation');
          }
        });
      }
    } catch (error) {
      this.log('⚠️ Error clearing stale ads:', error);
    }
  }

  /**
   * Notify Google AdSense of page change in SPA
   */
  notifyPageChange() {
    try {
      // Method 1: Use Google's recommended approach for SPAs
      if (window.adsbygoogle && typeof window.adsbygoogle.pauseAdRequests === 'function') {
        window.adsbygoogle.pauseAdRequests = 0;
        this.log('✅ Reset ad request pause state');
      }

      // Method 2: Trigger page view for auto ads (if supported)
      if (window.gtag) {
        window.gtag('config', 'GA_MEASUREMENT_ID', {
          page_path: window.location.pathname
        });
        this.log('✅ Sent page view event for auto ads analysis');
      }

      // Method 3: Use the safer approach - just re-enable auto ads without pushing
      if (window.adsbygoogle && window.adsbygoogle.loaded) {
        // Instead of pushing empty config, trigger re-evaluation
        const script = document.createElement('script');
        script.innerHTML = `
          if (window.adsbygoogle && window.adsbygoogle.loaded) {
            // Trigger auto ads re-evaluation for new content
            setTimeout(() => {
              if (typeof adsbygoogle !== 'undefined') {
                // This is safer than pushing to existing slots
                console.log('[AdSense Auto Ads] Page content changed - auto ads will re-evaluate');
              }
            }, 100);
          }
        `;
        document.head.appendChild(script);
        document.head.removeChild(script);
        this.log('✅ Triggered auto ads re-evaluation script');
      }

    } catch (error) {
      this.log('⚠️ Error notifying page change:', error);
    }
  }

  /**
   * Scan current page for auto ads opportunities and log findings
   */
  scanForAutoAdsOpportunities() {
    if (typeof document === 'undefined') return;

    const content = document.body.innerText;
    const contentLength = content.length;
    const paragraphs = document.querySelectorAll('p, .content, article').length;
    const images = document.querySelectorAll('img').length;
    const lists = document.querySelectorAll('ul, ol').length;

    this.log('🔍 Scanning page for auto ads opportunities:');
    this.log(`  • Content length: ${contentLength} chars`);
    this.log(`  • Text blocks: ${paragraphs}`);
    this.log(`  • Images: ${images}`);
    this.log(`  • Lists: ${lists}`);

    // Auto ads work better with substantial content
    if (contentLength > 1500 && paragraphs > 3) {
      this.log('✅ Page looks good for auto ads');
    } else if (contentLength > 800) {
      this.log('⚠️ Page has moderate potential for auto ads');
    } else {
      this.log('❌ Page may have limited auto ads potential');
    }

    // Check current route for specific improvements
    const currentPath = window.location.pathname;
    this.suggestImprovements(currentPath, contentLength, paragraphs);
  }

  /**
   * Suggest improvements for better auto ads performance
   */
  suggestImprovements(path, contentLength, paragraphs) {
    const suggestions = [];
    const issues = [];

    // Content length analysis
    if (contentLength < 1500) {
      if (contentLength < 800) {
        issues.push(`❌ Very low content (${contentLength} chars) - auto ads unlikely`);
        suggestions.push('Add much more content (aim for 1500+ characters)');
      } else {
        issues.push(`⚠️ Low content (${contentLength} chars) - auto ads limited`);
        suggestions.push('Add more content (aim for 1500+ characters for optimal auto ads)');
      }
    }

    if (paragraphs < 3) {
      issues.push('❌ Too few text blocks - auto ads need content structure');
      suggestions.push('Break content into more paragraphs and sections');
    }

    // Check existing manual ads vs content ratio
    const manualAds = document.querySelectorAll('ins.adsbygoogle:not([data-ad-format="auto"]):not([data-ad-format="autorelaxed"])');
    const adDensity = manualAds.length / (contentLength / 1000);

    if (adDensity > 3) {
      issues.push(`⚠️ High ad density (${adDensity.toFixed(1)} ads per 1000 chars) - may block auto ads`);
      suggestions.push('Consider reducing manual ad units to allow auto ads');
    }

    // Route-specific suggestions
    if (path.includes('/artist/') && contentLength < 1200) {
      suggestions.push('Add detailed artist biography, album reviews, song meanings');
    }

    if (path.includes('/release/') && contentLength < 1000) {
      suggestions.push('Add album description, track details, artist quotes, reviews');
    }

    if (path === '/' && contentLength < 1500) {
      suggestions.push('Add featured articles, news, artist spotlights to homepage');
    }

        // Display results
        if (issues.length > 0) {
          this.log('🚨 Auto Ads Issues Detected:');
          issues.forEach(issue => this.log(`  ${issue}`));
        }

        if (suggestions.length > 0) {
          this.log('💡 Auto Ads Improvement Suggestions:');
          suggestions.forEach((suggestion, index) => {
            this.log(`  ${index + 1}. ${suggestion}`);
          });
        }

        // AdSense account checks
        this.checkAdSenseAccountSettings();

        // Auto-trigger gentle method when content is low and auto ads are disabled by default
        // Removed automatic triggering to prevent duplicate ads
  }

  /**
   * Enable auto ads via data attributes when disabled by default
   * This method is more reliable for overriding account-level disabled settings
   */
  enableAutoAdsViaDataAttributes() {
    if (typeof document === 'undefined') return;

          this.log('🔄 Auto ads data attributes method skipped to prevent unwanted ads');
  }

  /**
   * Check for potential AdSense account issues
   */
  checkAdSenseAccountSettings() {
    this.log('🔍 AdSense Account Diagnostics:');
    this.log('  📋 Your situation: Auto ads disabled by default');
    this.log('  1. This is expected - you disabled auto ads for other subdomains');
    this.log('  2. We are using programmatic override methods');
    this.log('  3. Check console for "Force override" messages');
    this.log('  4. Auto ads may take 10-60 minutes to appear after enabling');
    this.log('  5. Try testing on mobile (auto ads prefer mobile)');
    this.log('  📱 Mobile testing tip: Open dev tools → device emulation');
  }

  /**
   * Create gentle auto ads - just 1 strategically placed ad at the bottom
   * This is a conservative approach for disabled-by-default accounts
   */
  createGentleAutoAds() {
    if (typeof document === 'undefined') return;

    this.log('🚀 Creating gentle auto ads (single ad at bottom)...');

    try {
      // Remove any previously created force ads to avoid duplicates
      const existingForceAds = document.querySelectorAll('.adsbygoogle[data-force-created="true"]');
      existingForceAds.forEach(ad => ad.remove());
      this.log(`🧹 Removed ${existingForceAds.length} existing force-created ads`);

      // Create only ONE ad at the very bottom
      this.createSingleBottomAd();

    } catch (error) {
      this.log('❌ Error in gentle auto ads creation:', error);
    }
  }

  /**
   * Force create auto ad elements when account-level auto ads are disabled
   * This is the most aggressive approach for overriding disabled auto ads
   */
  forceCreateAutoAdElements() {
    if (typeof document === 'undefined') return;

    this.log('🚀 Force creating auto ad elements (aggressive override)...');

    try {
      // Remove any previously created force ads to avoid duplicates
      const existingForceAds = document.querySelectorAll('.adsbygoogle[data-force-created="true"]');
      existingForceAds.forEach(ad => ad.remove());
      this.log(`🧹 Removed ${existingForceAds.length} existing force-created ads`);

      // Create multiple auto ad formats to maximize chances
      this.createInContentAutoAd();
      this.createAnchorAd();
      this.createMultiplexAd();
      this.createBannerAds();

    } catch (error) {
      this.log('❌ Error in force create auto ads:', error);
    }
  }

  /**
   * Create a single ad at the very bottom of the page
   */
  createSingleBottomAd() {
    const contentSelectors = [
      'main', '.main-content', '#main', '.content',
      '.container', '.wrapper', 'article', '.post-content'
    ];

    let insertionPoint = null;
    for (const selector of contentSelectors) {
      const element = document.querySelector(selector);
      if (element && element.children.length > 0) {
        insertionPoint = element;
        break;
      }
    }

    if (!insertionPoint) {
      insertionPoint = document.body;
    }

    // Create just ONE ad at the very bottom
    const adElement = document.createElement('ins');
    adElement.className = 'adsbygoogle';
    adElement.style.cssText = 'display:block; text-align:center; margin: 40px auto 20px; max-width: 728px; min-height: 90px; background: rgba(245,245,245,0.1); border: 1px dashed rgba(200,200,200,0.3);';
    adElement.setAttribute('data-ad-client', 'ca-pub-8326584585992829');
    adElement.setAttribute('data-ad-format', 'auto');
    adElement.setAttribute('data-full-width-responsive', 'true');
    adElement.setAttribute('data-force-created', 'true');

    // Insert at the very end
    insertionPoint.appendChild(adElement);

    // Push to AdSense
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      this.log('✅ Created single bottom auto ad');
    } catch (error) {
      this.log('⚠️ Failed to push bottom auto ad:', error);
    }
  }

  /**
   * Create a single, well-placed in-content ad
   */
  createSingleInContentAd() {
    const contentSelectors = [
      'main', '.main-content', '#main', '.content',
      '.container', '.wrapper', 'article', '.post-content'
    ];

    let insertionPoint = null;
    for (const selector of contentSelectors) {
      const element = document.querySelector(selector);
      if (element && element.children.length > 1) {
        insertionPoint = element;
        break;
      }
    }

    if (!insertionPoint) {
      insertionPoint = document.body;
    }

    // Create just ONE in-content ad, placed strategically
    const adElement = document.createElement('ins');
    adElement.className = 'adsbygoogle';
    adElement.style.cssText = 'display:block; text-align:center; margin: 30px auto; max-width: 728px; min-height: 90px; background: rgba(245,245,245,0.3); border: 1px dashed #ddd;';
    adElement.setAttribute('data-ad-client', 'ca-pub-8326584585992829');
    adElement.setAttribute('data-ad-format', 'auto');
    adElement.setAttribute('data-full-width-responsive', 'true');
    adElement.setAttribute('data-force-created', 'true');

    // Insert towards the end, never at the top - better UX
    const targetIndex = Math.max(insertionPoint.children.length - 2, Math.floor(insertionPoint.children.length * 0.7));
    if (targetIndex > 0 && insertionPoint.children[targetIndex]) {
      insertionPoint.insertBefore(adElement, insertionPoint.children[targetIndex]);
    } else {
      // Fallback: append at the very end
      insertionPoint.appendChild(adElement);
    }

    // Push to AdSense
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      this.log('✅ Created single strategic auto ad');
    } catch (error) {
      this.log('⚠️ Failed to push single auto ad:', error);
    }
  }

  /**
   * Create in-content auto ads
   */
  createInContentAutoAd() {
    const contentSelectors = [
      'main', '.main-content', '#main', '.content',
      '.container', '.wrapper', 'article', '.post-content'
    ];

    let insertionPoint = null;
    for (const selector of contentSelectors) {
      const element = document.querySelector(selector);
      if (element && element.children.length > 2) {
        insertionPoint = element;
        break;
      }
    }

    if (!insertionPoint) {
      // Fallback to body
      insertionPoint = document.body;
    }

    // Create multiple in-content ads
    for (let i = 0; i < 3; i++) {
      const adElement = document.createElement('ins');
      adElement.className = 'adsbygoogle';
      adElement.style.cssText = 'display:block; text-align:center; margin: 20px 0; min-height: 90px; background: #f5f5f5; border: 1px solid #e0e0e0;';
      adElement.setAttribute('data-ad-client', 'ca-pub-8326584585992829');
      adElement.setAttribute('data-ad-format', 'fluid');
      adElement.setAttribute('data-ad-layout', 'in-article');
      adElement.setAttribute('data-ad-layout-key', '-6t+ed+2i-1n-4w');
      adElement.setAttribute('data-force-created', 'true');

      // Insert at different positions
      const targetIndex = Math.min(i * 2 + 1, insertionPoint.children.length);
      if (insertionPoint.children[targetIndex]) {
        insertionPoint.insertBefore(adElement, insertionPoint.children[targetIndex]);
      } else {
        insertionPoint.appendChild(adElement);
      }

      // Push to AdSense
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        this.log(`✅ Created in-content auto ad ${i + 1}`);
      } catch (error) {
        this.log(`⚠️ Failed to push in-content auto ad ${i + 1}:`, error);
      }
    }
  }

  /**
   * Create anchor (sticky bottom) ad
   */
  createAnchorAd() {
    // Check if anchor ad already exists
    if (document.querySelector('.adsbygoogle[data-anchor-shown="true"]')) {
      this.log('ℹ️ Anchor ad already exists');
      return;
    }

    const anchorAd = document.createElement('ins');
    anchorAd.className = 'adsbygoogle';
    anchorAd.style.cssText = 'display:block;position:fixed;bottom:0;left:0;width:100%;height:auto;z-index:1000;min-height:50px;background:rgba(245,245,245,0.9);border-top:1px dashed #ddd;max-height:100px;';
    anchorAd.setAttribute('data-ad-client', 'ca-pub-8326584585992829');
    anchorAd.setAttribute('data-ad-format', 'auto');
    anchorAd.setAttribute('data-ad-slot', '');
    anchorAd.setAttribute('data-force-created', 'true');

    document.body.appendChild(anchorAd);

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      this.log('✅ Created anchor (sticky bottom) ad');
    } catch (error) {
      this.log('⚠️ Failed to push anchor ad:', error);
    }
  }

  /**
   * Create multiplex (native) ads
   */
  createMultiplexAd() {
    // Find a good location for multiplex ad
    const goodLocations = document.querySelectorAll('main, .main-content, #main, .content');
    if (goodLocations.length === 0) return;

    const container = goodLocations[0];

    const multiplexAd = document.createElement('ins');
    multiplexAd.className = 'adsbygoogle';
    multiplexAd.style.cssText = 'display:block; margin: 20px 0; min-height: 200px; background: #f5f5f5; border: 1px solid #e0e0e0;';
    multiplexAd.setAttribute('data-ad-client', 'ca-pub-8326584585992829');
    multiplexAd.setAttribute('data-ad-format', 'autorelaxed');
    multiplexAd.setAttribute('data-matched-content-ui-type', 'image_stacked');
    multiplexAd.setAttribute('data-matched-content-columns-num', '1,2');
    multiplexAd.setAttribute('data-matched-content-rows-num', '2,2');
    multiplexAd.setAttribute('data-force-created', 'true');

    // Append at the end
    container.appendChild(multiplexAd);

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      this.log('✅ Created multiplex (native) ad');
    } catch (error) {
      this.log('⚠️ Failed to push multiplex ad:', error);
    }
  }

  /**
   * Create standard banner ads (most reliable format)
   */
  createBannerAds() {
    const contentAreas = document.querySelectorAll('main, .main-content, #main, .content, body');
    if (contentAreas.length === 0) return;

    const container = contentAreas[0];

    // Create a leaderboard banner at the top
    const topBanner = document.createElement('ins');
    topBanner.className = 'adsbygoogle';
    topBanner.style.cssText = 'display:block; width:100%; height:90px; margin: 20px 0; background: #f5f5f5; border: 1px solid #e0e0e0;';
    topBanner.setAttribute('data-ad-client', 'ca-pub-8326584585992829');
    topBanner.setAttribute('data-ad-slot', ''); // Empty slot for auto-selection
    topBanner.setAttribute('data-ad-format', 'auto');
    topBanner.setAttribute('data-full-width-responsive', 'true');
    topBanner.setAttribute('data-force-created', 'true');

    // Insert at the beginning
    if (container.children.length > 0) {
      container.insertBefore(topBanner, container.children[0]);
    } else {
      container.appendChild(topBanner);
    }

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      this.log('✅ Created top banner ad');
    } catch (error) {
      this.log('⚠️ Failed to push top banner ad:', error);
    }

    // Create a rectangle banner in the middle
    const midBanner = document.createElement('ins');
    midBanner.className = 'adsbygoogle';
    midBanner.style.cssText = 'display:block; width:300px; height:250px; margin: 20px auto; background: #f5f5f5; border: 1px solid #e0e0e0;';
    midBanner.setAttribute('data-ad-client', 'ca-pub-8326584585992829');
    midBanner.setAttribute('data-ad-slot', ''); // Empty slot for auto-selection
    midBanner.setAttribute('data-ad-format', 'auto');
    midBanner.setAttribute('data-force-created', 'true');

    // Insert in the middle
    const midIndex = Math.floor(container.children.length / 2);
    if (container.children[midIndex]) {
      container.insertBefore(midBanner, container.children[midIndex]);
    } else {
      container.appendChild(midBanner);
    }

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      this.log('✅ Created middle banner ad');
    } catch (error) {
      this.log('⚠️ Failed to push middle banner ad:', error);
    }
  }

  /**
   * Create a single strategic auto ad that will be visible
   * This ensures ads appear even when auto ads are disabled by default
   */
  createSingleStrategicAd() {
    if (typeof document === 'undefined') return;

    this.log('🎯 Creating single strategic auto ad for visibility...');

    try {
      // Remove any existing strategic ads to prevent duplicates
      const existingStrategicAds = document.querySelectorAll('.adsbygoogle[data-strategic="true"]');
      existingStrategicAds.forEach(ad => ad.remove());
      this.log(`🧹 Removed ${existingStrategicAds.length} existing strategic ads`);

      // Find the best content container
      const contentSelectors = [
        'main', '.main-content', '#main', '.content',
        '.container', '.wrapper', 'article', '.post-content', 'body'
      ];

      let insertionPoint = null;
      for (const selector of contentSelectors) {
        const element = document.querySelector(selector);
        if (element && element.children.length > 2) {
          insertionPoint = element;
          break;
        }
      }

      if (!insertionPoint) {
        this.log('⚠️ No suitable insertion point found for strategic ad');
        return;
      }

      // Create a single, responsive auto ad
      const adElement = document.createElement('ins');
      adElement.className = 'adsbygoogle';
      adElement.style.cssText = `
        display: block;
        margin: 30px auto;
        text-align: center;
        max-width: 100%;
        min-height: 90px;
        background: rgba(248, 249, 250, 0.5);
        border: 1px dashed #dee2e6;
        border-radius: 4px;
        position: relative;
      `;

      // Set attributes for responsive auto ad
      adElement.setAttribute('data-ad-client', 'ca-pub-8326584585992829');
      adElement.setAttribute('data-ad-format', 'auto');
      adElement.setAttribute('data-full-width-responsive', 'true');
      adElement.setAttribute('data-strategic', 'true');

      // Add a subtle loading indicator
      adElement.innerHTML = '<div style="padding: 20px; color: #6c757d; font-size: 12px; font-family: Arial, sans-serif;">Advertisement</div>';

      // Insert at 70% down the content (good placement without blocking main content)
      const targetIndex = Math.floor(insertionPoint.children.length * 0.7);
      if (targetIndex > 0 && insertionPoint.children[targetIndex]) {
        insertionPoint.insertBefore(adElement, insertionPoint.children[targetIndex]);
      } else {
        insertionPoint.appendChild(adElement);
      }

      // Push to AdSense after a brief delay
      setTimeout(() => {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          this.log('✅ Strategic auto ad created and pushed to AdSense');
        } catch (error) {
          this.log('⚠️ Failed to push strategic ad to AdSense:', error);
        }
      }, 500);

    } catch (error) {
      this.log('❌ Error creating strategic auto ad:', error);
    }
  }

  /**
   * Check if auto ads are enabled
   * @returns {boolean} True if auto ads are enabled
   */
  isEnabled() {
    return this.isAutoAdsEnabled;
  }

  /**
   * Check if AdSense is loaded
   * @returns {boolean} True if AdSense script is loaded
   */
  isLoaded() {
    return this.isAdSenseLoaded;
  }

  /**
   * Log debug messages if debug mode is enabled
   * @param {...any} args - Arguments to log
   */
  log(...args) {
    if (this.debugMode) {
      console.log('[AdSense Auto Ads]', ...args);
    }
  }

  /**
   * Get detailed status information about auto ads
   * @returns {Object} Status object with detailed information
   */
  getStatus() {
    return {
      isAutoAdsEnabled: this.isAutoAdsEnabled,
      isAdSenseLoaded: this.isAdSenseLoaded,
      autoAdsStatus: this.autoAdsStatus,
      initAttempts: this.initAttempts,
      lastError: this.lastError,
      adSenseQueue: typeof window !== 'undefined' ? window.adsbygoogle?.length || 0 : 0,
      pageUrl: typeof window !== 'undefined' ? window.location.href : 'N/A',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'
    };
  }

  /**
   * Check and log current auto ads status
   */
  checkAutoAdsStatus() {
    const status = this.getStatus();
    this.log('🔍 Auto Ads Status Check:', status);

    // Check for AdSense elements in DOM
    if (typeof document !== 'undefined') {
      const adsenseElements = document.querySelectorAll('ins.adsbygoogle');
      const autoAdsElements = document.querySelectorAll('[data-ad-client]');

      this.log(`📊 Found ${adsenseElements.length} AdSense elements in DOM`);
      this.log(`🤖 Found ${autoAdsElements.length} auto ads containers in DOM`);

      // Check if any ads are actually rendered
      let renderedAds = 0;
      adsenseElements.forEach(el => {
        if (el.innerHTML.trim() !== '') {
          renderedAds++;
        }
      });

      this.log(`✅ ${renderedAds} ads appear to be rendered`);

      if (renderedAds === 0 && this.isAutoAdsEnabled) {
        this.log('⚠️ Warning: Auto ads enabled but no rendered ads detected');
        this.log('💡 This could be normal - auto ads may take time to appear or may not show based on content/traffic');
      }
    }

    return status;
  }

  /**
   * Create a visual debug panel for auto ads status
   */
  createDebugPanel() {
    if (typeof document === 'undefined') return;

    // Remove existing debug panel
    const existingPanel = document.getElementById('adsense-debug-panel');
    if (existingPanel) {
      existingPanel.remove();
    }

    const panel = document.createElement('div');
    panel.id = 'adsense-debug-panel';
    panel.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: #000;
      color: #fff;
      padding: 15px;
      border-radius: 8px;
      font-family: monospace;
      font-size: 12px;
      z-index: 10000;
      max-width: 300px;
      border: 2px solid #333;
    `;

    const status = this.getStatus();
    const statusColor = status.isAutoAdsEnabled ? '#4CAF50' : '#F44336';

    panel.innerHTML = `
      <div style="margin-bottom: 10px; font-weight: bold; color: ${statusColor}">
        🤖 Auto Ads Debug Panel
      </div>
      <div><strong>Status:</strong> ${status.autoAdsStatus}</div>
      <div><strong>Enabled:</strong> ${status.isAutoAdsEnabled ? '✅' : '❌'}</div>
      <div><strong>AdSense Loaded:</strong> ${status.isAdSenseLoaded ? '✅' : '❌'}</div>
      <div><strong>Queue Length:</strong> ${status.adSenseQueue}</div>
      <div><strong>Init Attempts:</strong> ${status.initAttempts}</div>
      ${status.lastError ? `<div style="color: #F44336;"><strong>Last Error:</strong> ${status.lastError.message}</div>` : ''}
      <div style="margin-top: 10px;">
        <button onclick="window.adSenseAutoAds.checkAutoAdsStatus()" style="padding: 5px 10px; margin-right: 5px; background: #2196F3; color: white; border: none; border-radius: 3px; cursor: pointer;">
          Refresh
        </button>
        <button onclick="document.getElementById('adsense-debug-panel').remove()" style="padding: 5px 10px; background: #F44336; color: white; border: none; border-radius: 3px; cursor: pointer;">
          Close
        </button>
      </div>
    `;

    document.body.appendChild(panel);
    this.log('🔧 Debug panel created');
  }

  /**
   * Enable or disable debug mode
   * @param {boolean} enabled - Whether to enable debug mode
   */
  setDebugMode(enabled) {
    this.debugMode = enabled;
    this.log(`Debug mode ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Create a singleton instance
const adSenseAutoAds = new AdSenseAutoAds();

// Expose globally for debugging
if (typeof window !== 'undefined') {
  window.adSenseAutoAds = adSenseAutoAds;

  // Add manual testing methods
  window.testAutoAds = () => {
    console.log('🧪 Manual auto ads test triggered (strategic)...');
    adSenseAutoAds.createSingleStrategicAd();
  };

  window.testAggressiveAutoAds = () => {
    console.log('🧪 Manual aggressive auto ads test triggered...');
    adSenseAutoAds.forceCreateAutoAdElements();
  };

  window.clearAutoAds = () => {
    console.log('🧹 Clearing created auto ads...');
    const createdAds = document.querySelectorAll('ins.adsbygoogle[data-force-created="true"]');
    createdAds.forEach(ad => ad.remove());
    console.log(`🗑️ Removed ${createdAds.length} force-created ads`);
  };
}

export default adSenseAutoAds;

